import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const GOLD = '#c8a04e';
const W  = (a) => `rgba(255,255,255,${a})`;
const GA = (a) => `rgba(200,160,78,${a})`;

const CAUSES = [
  {
    title: 'Insufficient balance on your card',
    desc: 'Your card may not have had enough funds at the time of the transaction.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,80,80,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  {
    title: 'An issue with our website',
    desc: 'A temporary error on our end may have interrupted the checkout process.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,80,80,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  },
  {
    title: 'An issue with Stripe payments',
    desc: 'Stripe may be experiencing a temporary outage. You can check their status at status.stripe.com.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,80,80,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
];

const gridLines = [
  { left: '10%', delay: '0s' }, { left: '25%', delay: '2.2s' },
  { left: '42%', delay: '4.5s' }, { left: '58%', delay: '0.8s' },
  { left: '75%', delay: '3.1s' }, { left: '90%', delay: '1.6s' },
];

export default function PurchaseFailed() {
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      <style>{`@keyframes pfDrop{0%{top:-20%}100%{top:110%}}`}</style>

      {/* ── LEFT: Possible causes + Discord ── */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px' }}>

        {/* Grid */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          {gridLines.map(({ left, delay }, i) => (
            <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: W(0.055), overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: `linear-gradient(to bottom,${GA(0)} 0%,${GA(0.65)} 75%,${GA(0.85)} 100%)`, animation: `pfDrop 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)` }} />
            </div>
          ))}
          {[0,1,2,3,4,5].map(i => <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${Math.round((i/5)*100)}%`, height: 1, background: W(0.04) }} />)}
        </div>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
        {/* Right edge fade */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right,transparent,#000)', zIndex: 2, pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 3 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,80,80,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>Payment Failed</p>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 48px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            What could have<br/>gone wrong?
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {CAUSES.map(({ title, desc, icon }, idx) => (
              <div key={idx}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,80,80,0.07)', border: '1px solid rgba(255,80,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div style={{ paddingTop: 2 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 5 }}>{title}</div>
                    <div style={{ fontSize: 13, color: W(0.35), lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
                {idx < CAUSES.length - 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, height: 40 }}>
                    <div style={{ width: 1, height: '100%', background: 'rgba(255,80,80,0.18)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Discord support */}
          <div style={{ marginTop: 40 }}>
            <a href="https://discord.gg/9KWNw8CeZX" target="_blank" rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '11px 20px', borderRadius: 10, background: 'rgba(88,101,242,0.1)', border: '1px solid rgba(88,101,242,0.25)', color: 'rgba(148,160,255,0.9)', fontWeight: 600, fontSize: 13.5, textDecoration: 'none', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(88,101,242,0.18)'; e.currentTarget.style.borderColor = 'rgba(88,101,242,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(88,101,242,0.1)'; e.currentTarget.style.borderColor = 'rgba(88,101,242,0.25)'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(148,160,255,0.8)"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
              Contact Support on Discord
            </a>
          </div>
        </div>

      </div>

      {/* ── DIVIDER ── */}
      <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── RIGHT: Failed panel ── */}
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 56px', overflow: 'hidden', position: 'relative' }}>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: 'easeOut' }} style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>

          <div style={{ background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.07) 0%,rgba(255,255,255,0.03) 55%,rgba(255,255,255,0.015) 100%)', border: `1px solid ${W(0.1)}`, borderRadius: 20, padding: '40px', boxShadow: `0 24px 60px rgba(0,0,0,0.6),inset 0 1px 0 ${W(0.06)}`, textAlign: 'center' }}>

            {/* X icon */}
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 16, delay: 0.1 }}
              style={{ width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.18)', boxShadow: '0 0 28px rgba(255,80,80,0.08)', margin: '0 auto 20px' }}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="12" stroke="rgba(255,80,80,0.35)" strokeWidth="1.4" />
                <path d="M9.5 9.5l9 9M18.5 9.5l-9 9" stroke="rgba(255,80,80,0.8)" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Payment Not Completed</h1>
              <p style={{ fontSize: 13, color: W(0.32), lineHeight: 1.65, margin: '0 0 28px' }}>
                Your payment didn't go through.<br/>No charges were made to your account.
              </p>
            </motion.div>

            <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${W(0.07)},transparent)`, marginBottom: 24 }} />

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.28 }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/#pricing"
                style={{ display: 'block', padding: '12px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.3)}`, boxShadow: `inset 0 1px 0 ${GA(0.1)}`, color: GOLD, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.2) 0%,rgba(200,160,78,0.08) 55%,rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = GA(0.45); }}
                onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.3); }}>
                Try Again
              </Link>
              <Link to="/dashboard"
                style={{ display: 'block', padding: '12px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.06) 0%,rgba(255,255,255,0.025) 55%,rgba(255,255,255,0.012) 100%)', border: `1px solid ${W(0.09)}`, boxShadow: `inset 0 1px 0 ${W(0.05)}`, color: W(0.5), fontWeight: 600, fontSize: 13.5, textDecoration: 'none', transition: 'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.1) 0%,rgba(255,255,255,0.04) 55%,rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.color = W(0.85); }}
                onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.06) 0%,rgba(255,255,255,0.025) 55%,rgba(255,255,255,0.012) 100%)'; e.currentTarget.style.color = W(0.5); }}>
                Go to Dashboard
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <Link to="/" style={{ position: 'absolute', bottom: 28, fontSize: 12, color: W(0.2), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s' }}
          onMouseEnter={e => e.currentTarget.style.color = W(0.5)} onMouseLeave={e => e.currentTarget.style.color = W(0.2)}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}
